import { type CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import { BUCKETS, type AdvanceView, type FinanceDetail as FinanceDetailData, type InvoiceView } from "../../api/finance";
import { BOJE, bucketLabel, bucketValue } from "./AgingChart";
import { KPI, Tag } from "../../components/primitives.jsx";
import { money, fmtDate, docnjaDana } from "./money";
import { IznosSaValutama, iznos } from "./CurrencyBreakdown";
import { formatMoneyCode, intlLocale } from "../../lib/format";
import { CreditCheckCard } from "../../components/CreditCheckCard";
import { TurnoverChart } from "./TurnoverChart";
import { InvoiceDetailModal } from "./InvoiceDetailModal";

/** The full per-company finance view: KPIs, aging bar, invoices, payments, turnover.
 * Shared by the customer self-view and the internal drill-down. */
export function FinanceDetail({
  data,
  strana = "kupac",
}: {
  data: FinanceDetailData;
  /** Prosleđuje se modalu, da poreklo robe čita sa prave strane. */
  strana?: "kupac" | "dobavljac";
}) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const cur = data.account.currency || "EUR";
  // Balance and overdue are every account of the company, in euro at the latest rate; the split
  // under them is each account in its own currency, and the note names the day of that rate.
  const kursOd = data.account.rateDate
    ? t("finance.kpi.rateDate", "at the rate of {{datum}}", { datum: fmtDate(data.account.rateDate) })
    : undefined;
  // Overdue and the aging are counted on the day the data reaches, which can be earlier than today;
  // the card names that day beside both, so the figure is not read as standing for today.
  const naDan = data.asOf
    ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.asOf) })
    : undefined;
  const [selected, setSelected] = useState<InvoiceView | null>(null);
  // Grupe, njihova imena i boje dolaze sa spiska potraživanja: isti pragovi i isti izgled, da
  // se ista tražbina ne bi na dva ekrana činila drugačijom.
  //
  // PO VALUTI, jedna traka za svaku. Dok je bila jedna, firma sa 711,08 EUR i 40.321,08 USD
  // dobijala je kofu od 41.032,16 označenu dolarom — jer je valuta uzeta sa „glavnog" naloga.
  // Strana je tada sama sebe obarala: zbir kofa 41.033,78 prema KPI saldu 40.321,08.
  const trake = data.aging.map((a) => {
    const kofe = BUCKETS.map((b) => ({
      key: b,
      label: bucketLabel(b, t),
      boja: BOJE[b],
      v: bucketValue(
        { notDueEur: a.notDue, d7Eur: a.d7, d30Eur: a.d30, d60Eur: a.d60,
          d120Eur: a.d120, d120PlusEur: a.d120Plus,
          carriedEur: a.carried },
        b,
      ),
    }));
    return { valuta: a.currency, kofe, ukupno: kofe.reduce((s, b) => s + (b.v || 0), 0) || 1 };
  });
  // A month below zero is turnover too: more was cancelled in it than invoiced.
  const hasTurnover = data.turnover.periods.some((p) => p.invoicedEur !== 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="kpi-row" style={kpiRow}>
        <KPI
          label={t("finance.kpi.balance", "Balance")}
          value={<IznosSaValutama eur={data.account.balanceTotal} byCurrency={data.account.byCurrency ?? []}
                                  uzmi={(v) => v.balance} locale={locale} napomena={kursOd} tacno />}
          delta={undefined} trend={undefined} icon={undefined}
        />
        {/* The advance stands next to the balance and is not taken from it: the ERP keeps it on
            accounts of its own. The API sends it only on the internal card, and only when there is
            an amount, so the tile never reads zero. */}
        {data.advanceReceived && data.advanceReceived.byCurrency.length > 0 && (
          <KPI
            label={t("finance.kpi.advanceReceived", "Advance received")}
            value={<PrimljeniAvans avans={data.advanceReceived} locale={locale} />}
            delta={undefined} trend={undefined} icon={undefined}
          />
        )}
        <KPI
          label={t("finance.kpi.overdue", "Overdue")}
          value={<>
            <IznosSaValutama eur={data.account.overdueTotal} byCurrency={data.account.byCurrency ?? []}
                             uzmi={(v) => v.overdue} locale={locale} napomena={kursOd} tacno />
            {naDan && <span style={naDanStil}>{naDan}</span>}
          </>}
          delta={undefined} trend={undefined} icon={undefined}
        />
        {/* Bonitet uz saldo i dospelo: pitanje „koliko duguje" i pitanje „hoće li platiti" gledaju
            se zajedno, pa stoje u istom redu. Ista komponenta kao na stranici firme, u zbijenom
            obliku — podaci i pragovi ne smeju da se razilaze između dva ekrana. */}
        <CreditCheckCard companyId={data.company.id} compact />
        {/* Credit fields are an internal risk figure: the API nulls them on the
            customer's own view, so the tiles only render for internal callers. */}
        {data.account.creditLimit != null && (
          <KPI label={t("finance.kpi.creditLimit", "Credit limit")} value={money(data.account.creditLimit, cur)} delta={undefined} trend={undefined} icon={undefined} />
        )}
        {data.account.creditAvailable != null && (
          <KPI label={t("finance.kpi.creditAvailable", "Credit available")} value={money(data.account.creditAvailable, cur)} delta={undefined} trend={undefined} icon={undefined} />
        )}
      </div>

      {/* Aging bar, po valuti */}
      {trake.length > 0 && (
        <section style={card}>
          <h2 style={sectionTitle}>{t("finance.aging.title", "Aging of open amounts")}</h2>
          {naDan && <div style={{ ...naDanStil, margin: "-6px 0 10px" }}>{naDan}</div>}
          {trake.map((traka) => (
            <div key={traka.valuta} style={{ marginTop: trake.length > 1 ? 12 : 0 }}>
              {/* Oznaka valute stoji samo kad ih ima više: jednoj firmi sa jednom valutom nov
                  natpis ne govori ništa, a sa dve je razlika između dve trake jedino što znači. */}
              {trake.length > 1 && (
                <div style={{ color: "var(--ink-3)", fontSize: 12, marginBottom: 4 }}>{traka.valuta}</div>
              )}
              <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", background: "var(--surface-2)" }}>
                {traka.kofe.map((b) =>
                  b.v > 0 ? (
                    <div key={b.key} title={`${b.label}: ${money(b.v, traka.valuta)}`}
                      style={{ width: `${(b.v / traka.ukupno) * 100}%`, background: b.boja }} />
                  ) : null,
                )}
              </div>
              <div style={agingLegend}>
                {traka.kofe.map((b) => (
                  <div key={b.key} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ color: "var(--ink-3)" }}>{b.label}</span>
                    <b>{money(b.v, traka.valuta)}</b>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Turnover */}
      {hasTurnover && (
        <section style={card}>
          <h2 style={sectionTitle}>{t("finance.turnover.title", "Turnover")}</h2>
          <TurnoverChart periods={data.turnover.periods} currency="EUR" />
        </section>
      )}

      {/* Invoices */}
      <section style={card}>
        <h2 style={sectionTitle}>{t("finance.invoices.title", "Invoices")}</h2>
        {data.invoices.length === 0 ? (
          <div className="empty">{t("finance.invoices.empty", "No invoices in the selected period.")}</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("finance.invoices.number", "Invoice")}</th>
                <th>{t("finance.invoices.issueDate", "Issued")}</th>
                <th>{t("finance.invoices.dueDate", "Due")}</th>
                <th style={{ textAlign: "right" }}>{t("finance.invoices.gross", "Amount")}</th>
                <th style={{ textAlign: "right" }}>{t("finance.invoices.open", "Open")}</th>
                <th>{t("finance.invoices.status", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((i) => (
                <tr
                  key={i.id}
                  style={{ cursor: "pointer" }}
                  tabIndex={0}
                  role="button"
                  aria-label={t("finance.invoices.viewDetail", "View invoice details")}
                  onClick={() => setSelected(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(i);
                    }
                  }}
                >
                  <td>{i.invoiceNumber}</td>
                  <td>{fmtDate(i.issueDate)}</td>
                  <td>{fmtDate(i.dueDate)}</td>
                  <td style={{ textAlign: "right" }}>{money(i.grossAmount, i.currency)}</td>
                  <td style={{ textAlign: "right" }}>{money(i.openAmount, i.currency)}</td>
                  <td>
                    {i.overdue ? (
                      // Uz „Dospelo" ide i koliko: razlika između sedam i sto sedam dana je
                      // razlika između opomene i naplate, a inače se računa iz dva datuma u glavi.
                      <Tag kind="err" dot={undefined} style={undefined}>
                        <Docnja dueDate={i.dueDate} asOf={data.asOf} />
                      </Tag>
                    ) : (
                      <Tag kind="neutral" dot={undefined} style={undefined}>{t(`finance.status.${i.status}`, i.status)}</Tag>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Payments */}
      <section style={card}>
        <h2 style={sectionTitle}>{t("finance.payments.title", "Payments")}</h2>
        {data.payments.length === 0 ? (
          <div className="empty">{t("finance.payments.empty", "No payments in the selected period.")}</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("finance.payments.date", "Date")}</th>
                <th style={{ textAlign: "right" }}>{t("finance.payments.amount", "Amount")}</th>
                <th>{t("finance.payments.method", "Method")}</th>
                <th>{t("finance.payments.invoice", "Invoice")}</th>
                <th>{t("finance.payments.reference", "Reference")}</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.paymentDate)}</td>
                  <td style={{ textAlign: "right" }}>{money(p.amount, p.currency)}</td>
                  <td>{p.method ?? "—"}</td>
                  <td>{p.invoiceNumber ?? "—"}</td>
                  <td>{p.reference ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected && (
        <InvoiceDetailModal invoice={selected} payments={data.payments} strana={strana}
                            asOf={data.asOf} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

/**
 * What the customer paid ahead, in each currency as the ERP holds it, with the day of the snapshot
 * it comes from. Each currency stands on its own line: there is no sum across them to show.
 */
function PrimljeniAvans({ avans, locale }: { avans: AdvanceView; locale: string }) {
  const { t } = useTranslation();
  return (
    <span style={{ display: "block" }}>
      {avans.byCurrency.map((a) => (
        <span key={a.currency} style={iznos}>{formatMoneyCode(a.amount, a.currency, locale)}</span>
      ))}
      <span style={stanjeNa}>
        {t("finance.kpi.advanceDate", "as of {{datum}}", { datum: fmtDate(avans.snapshotDate) })}
      </span>
    </span>
  );
}

/**
 * „Dospelo" i koliko dana kasni.
 *
 * Bez broja dana oznaka kaže samo da je rok prošao, a razlika između sedam i sto sedam dana
 * odlučuje da li se šalje opomena ili se zove. Datum dospeća stoji u redu pored, ali računanje
 * razlike u glavi je posao koji ekran može da preuzme.
 */
function Docnja({ dueDate, asOf }: { dueDate: string | null; asOf?: string | null }) {
  const { t } = useTranslation();
  // Counted to the same day as the aging, so a bill does not read as later than its band.
  const dana = docnjaDana(dueDate, asOf ? new Date(asOf + "T00:00:00") : undefined);
  if (dana == null) return <>{t("finance.invoices.overdue", "Overdue")}</>;
  return <>{t("finance.invoices.overdueDays", "Dospelo, {{count}} dana", { count: dana })}</>;
}

// Uža kolona je pre boniteta bila dovoljna; sada u redu stoji i kartica sa oznakama
// upozorenja, kojoj na 160px tekst prelama na tri reda.
const kpiRow: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 };
const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", boxShadow: "var(--shadow-1)", padding: 18,
};
const sectionTitle: CSSProperties = {
  margin: "0 0 12px", fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5,
};
const naDanStil: CSSProperties = { display: "block", fontSize: 11, color: "var(--ink-3)", marginTop: 4 };
const agingLegend: CSSProperties = {
  marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "6px 18px", fontSize: 12.5,
};
/** The day of the snapshot, set off under the amounts the way the day of the rate is on the balance. */
const stanjeNa: CSSProperties = {
  display: "block", fontSize: 11, fontWeight: 400, color: "var(--ink-3)", lineHeight: 1.45,
  marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)",
};
